import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { useSelector, useDispatch } from 'react-redux';
import { getAccessToken } from '@selectors/auth';
import { setLocalPhoneNumber } from '@actions/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { segment } from '@actions/analytics';
import Image from '@base/Image';
import Button from '@base/Button/Button';
import H3 from '@base/H3/H3';
import TextField from '@base/TextField/TextField';
import CountryMenu from '@cont/Login/CountryMenu';
import InputAdornment from '@material-ui/core/InputAdornment';
import useCountries from '@api/hooks/useCountries';
import useFreeTrialConfig from '@api/hooks/home/useFreeTrialConfig';
import Typography from 'aquilla/core/atoms/Typography';
import H5 from '@base/H5/H5';
import { FREE_TRIAL_DAYS } from '@constants/index';
import { FREE_TRIAL_ACTIVATE } from './constants/segment';
import Banner from './Banner';

const Heading = styled(H3)`
  margin-bottom: ${Spacings.SPACING_2B};
  text-align: left;
  ${mediaQuery.aboveMobile} {
    font-size: ${Fonts.H1};
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    font-size: ${Fonts.H3};
    text-align: start;
  }
`;

const StyButton = styled(Button)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_4B};
  width: 100%;
  ${mediaQuery.aboveMobile} {
    width: 75%;
    margin-top: ${Spacings.SPACING_10B};
  }
`;

const PhoneNumberField = styled(TextField)`
  width: 356px;
  background-color: var(--color-base-1);
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: -${Spacings.SPACING_4B};
  p {
    color: var(--color-text-primary);
    margin-left: ${Spacings.SPACING_0B};
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
  ${mediaQuery.aboveMobile} {
    width: 75%;
  }
  &.MuiOutlinedInput-root {
    .MuiOutlinedInput-input {
      padding: ${Spacings.SPACING_4B};
    }
  }
`;

const ImageWrapper = styled.div`
  ${mediaQuery.aboveMobile} {
    max-width: 501px;
    width: 50%;
  }
`;

const Wrapper = styled.div`
  background-color: var(--color-base-light-0);
  padding: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  text-align: center;
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_4B};

  ${mediaQuery.aboveMobile} {
    flex-direction: row-reverse;
    justify-content: space-between;
    background: none;
    margin-top: ${Spacings.SPACING_16B};
    margin-bottom: ${Spacings.SPACING_8B};
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const StyledTypography = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const SignupCrediteWrapper = styled.div`
  position: relative;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  display: flex;
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    justify-content: center;
  }
`;

const StepsWrapper = styled.div`
  margin-left: -${Spacings.SPACING_2};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StepInfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: ${Spacings.SPACING_2B};
`;

const StepInfo = styled(H5)`
  font-weight: ${Fonts.LIGHT};
  margin-left: ${Spacings.SPACING_6};
  max-width: calc(100% - 30px);
  text-align: left;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P1};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
`;

const StepIcon = styled(Image)`
  margin-top: ${Spacings.SPACING_2} !important;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_1} !important;
  }
`;

const HomeScreen = ({ selectGoal, setActivateClicked, referralCode }) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getAccessToken);
  const countries = useCountries();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [phone, onPhoneChange] = useState('');
  const [country, setCountry] = useState(
    countries.length
      ? countries.find((c) => c.code === 'IN')
      : {
          code: 'IN',
          name: 'India',
          flagIconUrl:
            'https://static.uacdn.net/thumbnail/country-flag-icons/in.png',
          phoneCode: '91'
        }
  );
  const freeTrialConfig = useFreeTrialConfig();

  const onNumberChange = (e) => {
    const number = e.target.value.replace(/ /g, '');
    if (number.match(/^\d+$/) || number === '') {
      if (number.length > 10) {
        // do nothing
      } else {
        onPhoneChange(number);
      }
    }
  };

  const changeCountry = (countryOption) => {
    setCountry(countries.find((c) => c.code === countryOption.value));
  };

  const validMobileNumber = useCallback(() => {
    if (phone.match(/^\d+$/) && phone.length !== 10) {
      return false;
    }
    if (phone === '') {
      return false;
    }
    return true;
  }, [phone]);

  const startFreeTrial = useCallback(() => {
    if (isLoggedIn) {
      selectGoal();
    } else if (validMobileNumber()) {
      setActivateClicked(true);
      dispatch(setLocalPhoneNumber(phone, country));
      openLoginDrawer();
    }
    segment.track(FREE_TRIAL_ACTIVATE, {
      free_trial_source: referralCode || 'NA'
    });
  }, [
    isLoggedIn,
    validMobileNumber,
    referralCode,
    selectGoal,
    setActivateClicked,
    dispatch,
    phone,
    country,
    openLoginDrawer
  ]);

  return (
    <>
      {freeTrialConfig?.showBanner && (
        <Banner bannerInfo={freeTrialConfig?.bannerImages} />
      )}
      <Wrapper>
        <ImageWrapper>
          <Image
            src="/free-trial/start-free-trial-img.svg"
            alt="start-free-trial"
            layout="responsive"
            width={328}
            height={218}
          />
        </ImageWrapper>
        <ContentWrapper>
          <div>
            <Heading>
              Start your {FREE_TRIAL_DAYS}-day Unlimited Plus access for free!
            </Heading>
            <StepsWrapper>
              <StepInfoWrapper>
                <StepIcon
                  src="educator/styled-check-light.svg"
                  width={20}
                  height={20}
                  alt="step"
                />
                <StepInfo>Access to all top educators and batches</StepInfo>
              </StepInfoWrapper>
              <StepInfoWrapper>
                <StepIcon
                  src="educator/styled-check-light.svg"
                  width={20}
                  height={20}
                  alt="step"
                />
                <StepInfo>
                  Unlimited practice, tests, doubt-solving classes and more
                </StepInfo>
              </StepInfoWrapper>
            </StepsWrapper>
          </div>
          {!isLoggedIn && (
            <PhoneNumberField
              fullWidth
              startAdornment={
                <InputAdornment>
                  <CountryMenu
                    changeCountry={changeCountry}
                    country={country}
                    countries={countries}
                  />
                </InputAdornment>
              }
              placeholder="Enter your mobile number"
              onChange={onNumberChange}
              value={phone}
              inputProps={{
                type: 'tel',
                maxLength: 15,
                minLength: 3,
                required: true
              }}
              type="tel"
            />
          )}
          <StyButton
            label="Activate free trial"
            variant={freeTrialConfig?.disableTrial ? 'disabled' : 'filled'}
            color="green"
            onClick={startFreeTrial}
          />
          <SignupCrediteWrapper>
            <Image src="stars_home.svg" width={16} height={16} alt="feature" />
            <StyledTypography variant="p2" color="var(--color-i-orange-22)">
              No payment needed
            </StyledTypography>
          </SignupCrediteWrapper>
        </ContentWrapper>
      </Wrapper>
    </>
  );
};

export default HomeScreen;
